import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Mail, Package } from 'lucide-react'

import { DashboardLayout } from '@/components/layouts/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { BrandListTable } from '@/components/features/suppliers/BrandListTable'
import { NewSupplierDialog } from '@/components/features/suppliers/NewSupplierDialog'
import { OrderPlatformsSection } from '@/components/features/suppliers/OrderPlatformsSection'
import { OrdersSpaceTabs } from '@/components/features/suppliers/OrdersSpaceTabs'
import { SupplierListTable } from '@/components/features/suppliers/SupplierListTable'
import { SupplierSheet } from '@/components/features/suppliers/SupplierSheet'
import { SupplierIgnoresDialog } from '@/components/features/suppliers/SupplierIgnoresDialog'
import { SupplierImportPanel } from '@/components/features/suppliers/SupplierImportPanel'
import { MODULES, useModules } from '@/hooks/useModules'
import { usePermissions } from '@/hooks/usePermissions'
import {
  getCurrentSupplierImport,
  getOrderPlatforms,
  getPurchaseBrands,
  getSuppliersOverview,
  removeSupplierEmailIgnore,
  type BrandOverviewDto,
  type SupplierEmailIgnoreDto,
  type OrderPlatformAddressDto,
  type SupplierOverviewDto,
} from '@/lib/api/v2/planning'
import { ApiError } from '@/lib/api/client'
import { PERMISSIONS } from '@/lib/permissions'
import { cn } from '@/lib/utils'

type View = 'suppliers' | 'brands'

interface DirectoryData {
  suppliers: SupplierOverviewDto[]
  brands: BrandOverviewDto[]
  platforms: OrderPlatformAddressDto[]
}

async function fetchDirectory(): Promise<DirectoryData> {
  const [suppliers, brands, platforms] = await Promise.all([getSuppliersOverview(), getPurchaseBrands(), getOrderPlatforms()])
  return { suppliers, brands, platforms }
}

function loadError(e: unknown): string {
  return e instanceof ApiError ? e.message : 'Les fournisseurs n’ont pas pu être chargés.'
}

/**
 * SOLY-SUPPLIER-002 MS-01 — the suppliers of the organization and the brands they supply, read both
 * ways (01-page-fournisseurs.md). One request per list, refreshed after every change: suppliers per
 * organization number in the dozens, a refetch is simpler and never stale.
 */
export default function SuppliersPage() {
  const { can, reasonIfDenied } = usePermissions()
  const canManage = can(PERMISSIONS.ORDERS_MANAGE)
  const manageHint = reasonIfDenied(PERMISSIONS.ORDERS_MANAGE)
  // SOLY-SUPPLIER-002 MS-03 — the import needs the module AND « gérer les commandes » (hidden otherwise).
  const { hasModule } = useModules()
  const canImport = canManage && hasModule(MODULES.PURCHASE_MAILBOX)
  const [searchParams, setSearchParams] = useSearchParams()
  // Back from connecting a first mailbox (?import=1): the import starts by itself.
  const [importAuto] = useState(() => searchParams.get('import') === '1')
  const [importRequested, setImportRequested] = useState(importAuto)
  // Each opening of the import is a new panel (its key): a finished import's screen never comes back.
  const [importSession, setImportSession] = useState(0)
  const requestImport = () => {
    setImportSession((n) => n + 1)
    setImportRequested(true)
  }
  const [ignoresOpen, setIgnoresOpen] = useState(false)
  const [restoring, setRestoring] = useState<SupplierEmailIgnoreDto | null>(null)

  const [view, setView] = useState<View>('suppliers')
  const [suppliers, setSuppliers] = useState<SupplierOverviewDto[] | null>(null)
  const [brands, setBrands] = useState<BrandOverviewDto[]>([])
  const [platforms, setPlatforms] = useState<OrderPlatformAddressDto[]>([])
  const [error, setError] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const apply = (data: DirectoryData) => {
    setSuppliers(data.suppliers)
    setBrands(data.brands)
    setPlatforms(data.platforms)
    setError(null)
  }

  // Two quick edits trigger two reloads that may answer out of order: only the latest request may
  // write the page, or an older answer resurrects a brand that was just removed.
  const latestRequest = useRef(0)

  // Event-driven reload (after a change, or « Réessayer »).
  const load = () => {
    const request = ++latestRequest.current
    return fetchDirectory()
      .then((data) => {
        if (request === latestRequest.current) apply(data)
      })
      .catch((e) => {
        if (request === latestRequest.current) setError(loadError(e))
      })
  }

  // Initial fetch: state is only set asynchronously, so the effect body never updates state itself.
  useEffect(() => {
    let active = true
    const request = ++latestRequest.current
    fetchDirectory()
      .then((data) => {
        if (active && request === latestRequest.current) apply(data)
      })
      .catch((e) => {
        if (active && request === latestRequest.current) setError(loadError(e))
      })
    // An import left running or waiting for its decision reopens with the page.
    if (canImport && !importAuto) {
      getCurrentSupplierImport()
        .then(({ import: current }) => {
          if (active && current && current.status !== 'Applied') setImportRequested(true)
        })
        .catch(() => {
          // The panel says nothing until asked: a failed check only means no automatic reopening.
        })
    }
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (searchParams.get('import')) setSearchParams({}, { replace: true })
  }, [searchParams, setSearchParams])

  const open = suppliers?.find((s) => s.id === openId) ?? null

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <div>
          <div className="flex items-center gap-2">
            <Package className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold">Fournisseurs</h1>
          </div>
          <p className="text-sm text-muted-foreground">Chez qui vous commandez, et quelles marques</p>
        </div>

        <OrdersSpaceTabs active="suppliers" />

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border p-0.5" role="group" aria-label="Vue">
            {(['suppliers', 'brands'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                aria-pressed={view === v}
                className={cn(
                  'rounded px-3 py-1 text-sm',
                  view === v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
                data-testid={`suppliers-view-${v}-btn`}
              >
                {v === 'suppliers' ? 'Par fournisseur' : 'Par marque'}
              </button>
            ))}
          </div>
          <div className="flex-1" />
          {canImport && (
            <>
              <Button variant="ghost" size="sm" onClick={() => setIgnoresOpen(true)} data-testid="suppliers-ignores-btn">
                Mes ignorés
              </Button>
              <Button variant="outline" onClick={requestImport} disabled={importRequested} data-testid="suppliers-import-btn">
                <Mail className="mr-1 h-4 w-4" />
                Retrouver dans mes emails
              </Button>
            </>
          )}
          <Button onClick={() => setCreating(true)} disabled={!canManage} title={manageHint} data-testid="suppliers-new-btn">
            Nouveau fournisseur
          </Button>
        </div>

        {error && (
          <div className="flex items-center gap-3 rounded-md border border-destructive/40 p-3 text-sm text-destructive">
            {error}
            <Button size="sm" variant="outline" onClick={() => void load()} data-testid="suppliers-retry-btn">
              Réessayer
            </Button>
          </div>
        )}

        {suppliers === null && !error && <Skeleton className="h-48 w-full" />}

        {canImport && importRequested && (
          <SupplierImportPanel
            key={importSession}
            requested
            // Only the opening that came back from connecting a mailbox starts by itself: any later one
            // shows the launch sentence first (an import costs an AI call).
            autoStart={importAuto && importSession === 0}
            suppliers={suppliers ?? []}
            onClosed={() => setImportRequested(false)}
            onApplied={() => void load()}
            onRefused={() => void load()}
            onOpenIgnores={() => setIgnoresOpen(true)}
          />
        )}

        {suppliers !== null && suppliers.length === 0 && view === 'suppliers' && (
          <div className="space-y-3 rounded-md border p-8 text-center text-sm text-muted-foreground" data-testid="suppliers-empty">
            <p>Aucun fournisseur pour l’instant.</p>
            {canImport && !importRequested && (
              <Button onClick={requestImport} data-testid="suppliers-empty-import-btn">
                <Mail className="mr-1 h-4 w-4" />
                Retrouver mes fournisseurs dans mes emails
              </Button>
            )}
          </div>
        )}

        {suppliers !== null && suppliers.length > 0 && view === 'suppliers' && (
          <SupplierListTable suppliers={suppliers} onOpen={setOpenId} />
        )}

        {suppliers !== null && view === 'brands' && (
          brands.length === 0 ? (
            <div className="rounded-md border p-8 text-center text-sm text-muted-foreground" data-testid="brands-empty">
              Aucune marque pour l’instant. Ajoutez-les depuis la fiche d’un fournisseur.
            </div>
          ) : (
            <BrandListTable brands={brands} suppliers={suppliers} canManage={canManage} manageHint={manageHint} onChanged={() => load()} />
          )
        )}

        {suppliers !== null && <OrderPlatformsSection platforms={platforms} canManage={canManage} onChanged={() => void load()} />}
      </div>

      <SupplierSheet
        supplier={open}
        suppliers={suppliers ?? []}
        platforms={platforms}
        brands={brands}
        canManage={canManage}
        manageHint={manageHint}
        onClose={() => setOpenId(null)}
        onChanged={() => load()}
      />

      <SupplierIgnoresDialog
        open={ignoresOpen}
        onOpenChange={setIgnoresOpen}
        onRestore={(ignore) => {
          setIgnoresOpen(false)
          setRestoring(ignore)
          setCreating(true)
        }}
      />

      <NewSupplierDialog
        key={restoring?.id ?? 'new'}
        initialName={restoring ? restoring.domain.slice(1).split('.')[0] : undefined}
        initialDomain={restoring?.domain}
        open={creating}
        onOpenChange={(o) => {
          setCreating(o)
          if (!o) setRestoring(null)
        }}
        suppliers={suppliers ?? []}
        onOpenExisting={(id) => {
          setCreating(false)
          setRestoring(null)
          setOpenId(id)
        }}
        onCreated={(id) => {
          // « l'ignoré disparaît une fois le fournisseur créé »
          if (restoring) void removeSupplierEmailIgnore(restoring.id).catch(() => undefined)
          setRestoring(null)
          void load().then(() => setOpenId(id))
        }}
      />
    </DashboardLayout>
  )
}
